'use client'

import { useEffect } from 'react'
import { toast } from 'sonner'
import { useTexts } from '@/contexts/LanguageContext'
import { Button } from '@/components/v2/ui/button'
import { Toaster } from '@/components/v2/ui/sonner'
import { DRAWER_STACKING } from '@/components/common/constants'

const SESSION_KEY = 'system_use_notice_shown'
const TOS_URL = 'https://www.qalea.ai/terms-and-conditions'
const PRIVACY_URL = 'https://www.qalea.ai/privacy-policy'
const TOASTER_ID = 'system-use-notice'
// Own toaster so the notice sits below drawers instead of above them (the global one is above everything)
const TOASTER_Z_INDEX = DRAWER_STACKING.PRIMARY_Z_INDEX - 1

type ISystemUseTexts = ReturnType<typeof useTexts>['SYSTEM_USE_NOTICE']

function wasShownThisSession(): boolean {
  return Boolean(sessionStorage.getItem(SESSION_KEY))
}

function markAsShown(): void {
  sessionStorage.setItem(SESSION_KEY, 'true')
}

function SystemUseToast({ id, texts }: { id: string | number; texts: ISystemUseTexts }) {
  return (
    <div className="flex flex-col gap-3 rounded-lg border bg-popover p-4 text-popover-foreground shadow-lg max-w-[480px]">
      <p className="text-sm font-semibold">{texts.TITLE}</p>
      <p className="text-xs leading-relaxed">{texts.MESSAGE}</p>
      <p className="text-xs leading-relaxed">
        {texts.MESSAGE_AUDIT}{' '}
        <a href={TOS_URL} target="_blank" rel="noopener noreferrer" className="underline font-medium">
          {texts.TERMS_OF_SERVICE}
        </a>{' '}
        {texts.AND}{' '}
        <a href={PRIVACY_URL} target="_blank" rel="noopener noreferrer" className="underline font-medium">
          {texts.PRIVACY_POLICY}
        </a>
        .
      </p>
      <div className="flex justify-end">
        <Button size="sm" variant="default" onClick={() => toast.dismiss(id)}>
          {texts.ACCEPT}
        </Button>
      </div>
    </div>
  )
}

export default function SystemUseNotice() {
  const { SYSTEM_USE_NOTICE } = useTexts()

  useEffect(() => {
    if (wasShownThisSession()) {
      return
    }
    markAsShown()

    toast.custom((id) => <SystemUseToast id={id} texts={SYSTEM_USE_NOTICE} />, {
      duration: Infinity,
      toasterId: TOASTER_ID,
    })
  }, [SYSTEM_USE_NOTICE])

  return <Toaster id={TOASTER_ID} position="bottom-right" style={{ zIndex: TOASTER_Z_INDEX }} />
}
